import { useCallback } from 'react';
import { InfiniteData } from '@antimatterhq/ui/hooks/queries/useInfiniteQuery';
import { useQueryClient } from '@antimatterhq/ui/hooks/queries/useQueryClient';
import { common_v1_ObjectMeta } from '@antimatterhq/asl-api-ts-v1';
import { ListResponse } from '@/types/listResponse';

export const useOptimisticDeleteFromInfiniteQuery = <
  TItem extends { metadata?: common_v1_ObjectMeta | null },
  TResponse extends ListResponse<TItem>,
>(
  queryKey: unknown[],
  limit: number,
  getItemKey?: (item: TItem) => string,
) => {
  const queryClient = useQueryClient();

  const optimisticDelete = useCallback(
    (item: TItem) => {
      queryClient.setQueryData(
        queryKey,
        (prev: InfiniteData<TResponse> | undefined) => {
          if (!prev) return prev;

          // Get all original items
          const originalItems = prev.pages.flatMap((page) => page.items);

          // Filter out the one to delete
          const updatedItems = originalItems.filter(
            getItemKey !== undefined
              ? (i) => getItemKey(i) !== getItemKey(item)
              : (i) => i.metadata?.name !== item.metadata?.name,
          );

          // Check if any item was actually removed
          if (updatedItems.length === originalItems.length) {
            // No item found/removed, return previous state unchanged
            return prev;
          }

          // Recalculate pages with the updated items
          const itemsPerPage = prev.pages[0]?.items.length ?? limit;
          const updatedPages: TResponse[] = [];

          for (let i = 0; i < updatedItems.length; i += itemsPerPage) {
            const pageItems = updatedItems.slice(i, i + itemsPerPage);
            const pageIndex = Math.floor(i / itemsPerPage);
            const originalPage = prev.pages[pageIndex];

            if (!originalPage) continue;

            updatedPages.push({
              ...originalPage,
              items: pageItems,
              metadata: {
                ...originalPage.metadata,
                // If the page is not the last page, keep the cursor
                cursor:
                  pageIndex < Math.ceil(updatedItems.length / itemsPerPage) - 1
                    ? originalPage.metadata?.cursor
                    : undefined,
              },
            } as TResponse);
          }

          // Filter out empty pages
          const filteredPages = updatedPages.filter(
            (page) => page.items.length > 0,
          );

          return {
            ...prev,
            pages: filteredPages,
          };
        },
      );
    },
    [queryClient, queryKey, limit, getItemKey],
  );

  return optimisticDelete;
};
